import { useState, useEffect, useCallback } from 'react'
import { adminService } from '../../services/adminService'
import {
  Key,
  Plus,
  Trash2,
  Copy,
  Check,
  AlertTriangle,
  Loader2,
  RefreshCw,
  X,
  ShieldAlert,
} from 'lucide-react'

// ─── Types ─────────────────────────────────────────────────────────────────────

interface APIKeyMetadata {
  id: string
  name: string
  permissionScope: string[]
  createdAt: string
  lastUsedAt: string | null
  status: 'active' | 'revoked'
}

type ModalType = 'create' | 'revoke' | 'showKey' | null

// ─── Constants ─────────────────────────────────────────────────────────────────

const AVAILABLE_SCOPES = [
  { value: 'read:users', label: 'Read Users' },
  { value: 'write:users', label: 'Write Users' },
  { value: 'read:businesses', label: 'Read Businesses' },
  { value: 'write:businesses', label: 'Write Businesses' },
  { value: 'read:scores', label: 'Read Scores' },
  { value: 'write:scores', label: 'Write Scores' },
  { value: 'read:lenders', label: 'Read Lenders' },
  { value: 'write:lenders', label: 'Write Lenders' },
  { value: 'read:analytics', label: 'Read Analytics' },
  { value: 'read:audit', label: 'Read Audit Log' },
  { value: 'manage:subscriptions', label: 'Manage Subscriptions' },
  { value: 'manage:system', label: 'Manage System Settings' },
]

// ─── Component ─────────────────────────────────────────────────────────────────

export function AdminAPIKeys() {
  // Data state
  const [keys, setKeys] = useState<APIKeyMetadata[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Modal state
  const [activeModal, setActiveModal] = useState<ModalType>(null)
  const [selectedKey, setSelectedKey] = useState<APIKeyMetadata | null>(null)
  const [modalLoading, setModalLoading] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)

  // Create form state
  const [keyName, setKeyName] = useState('')
  const [selectedScopes, setSelectedScopes] = useState<string[]>([])

  // Generated key state (shown once)
  const [generatedKey, setGeneratedKey] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  // ─── Data Fetching ─────────────────────────────────────────────────────────

  const fetchKeys = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await adminService.listAPIKeys()
      const data = response.data
      setKeys(Array.isArray(data) ? data : data?.items || [])
    } catch (err: any) {
      setError(err.message || 'Failed to load API keys')
      setKeys([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchKeys()
  }, [fetchKeys])

  // ─── Modal Handlers ────────────────────────────────────────────────────────

  const openCreateModal = () => {
    setKeyName('')
    setSelectedScopes([])
    setModalError(null)
    setActiveModal('create')
  }

  const openRevokeModal = (key: APIKeyMetadata) => {
    setSelectedKey(key)
    setModalError(null)
    setActiveModal('revoke')
  }

  const closeModal = () => {
    setActiveModal(null)
    setSelectedKey(null)
    setModalError(null)
    setGeneratedKey(null)
    setCopied(false)
  }

  // ─── Scope Selection ───────────────────────────────────────────────────────

  const toggleScope = (scope: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scope)
        ? prev.filter((s) => s !== scope)
        : [...prev, scope]
    )
  }

  // ─── CRUD Operations ───────────────────────────────────────────────────────

  const handleCreateKey = async () => {
    if (!keyName.trim()) {
      setModalError('Key name is required')
      return
    }
    if (selectedScopes.length === 0) {
      setModalError('Select at least one permission scope')
      return
    }
    setModalLoading(true)
    setModalError(null)
    try {
      const response = await adminService.createAPIKey({
        name: keyName.trim(),
        permissionScope: selectedScopes,
      })
      const data = response.data
      setGeneratedKey(data.key)
      setActiveModal('showKey')
      fetchKeys()
    } catch (err: any) {
      setModalError(err.message || 'Failed to create API key')
    } finally {
      setModalLoading(false)
    }
  }

  const handleRevokeKey = async () => {
    if (!selectedKey) return
    setModalLoading(true)
    setModalError(null)
    try {
      await adminService.revokeAPIKey(selectedKey.id)
      closeModal()
      fetchKeys()
    } catch (err: any) {
      setModalError(err.message || 'Failed to revoke API key')
    } finally {
      setModalLoading(false)
    }
  }

  // ─── Copy to Clipboard ─────────────────────────────────────────────────────

  const handleCopyKey = async () => {
    if (!generatedKey) return
    try {
      await navigator.clipboard.writeText(generatedKey)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = generatedKey
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  // ─── Render Helpers ────────────────────────────────────────────────────────

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—'
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    } catch {
      return '—'
    }
  }

  const getStatusBadge = (status: string) => {
    if (status === 'active') {
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
    }
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Key className="h-7 w-7 text-blue-600 dark:text-blue-400" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              API Key Management
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Create and manage API keys for system integrations
            </p>
          </div>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="h-4 w-4" />
          Create API Key
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          <button
            onClick={fetchKeys}
            className="ml-auto inline-flex items-center gap-1 text-sm text-red-700 dark:text-red-300 hover:underline"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Retry
          </button>
        </div>
      )}

      {/* API Keys Table */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" />
            <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
              Loading API keys...
            </span>
          </div>
        ) : keys.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <Key className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              No API keys created yet
            </p>
            <button
              onClick={openCreateModal}
              className="mt-3 text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              Create your first API key
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                    Name
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                    Created
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                    Last Used
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                    Permission Scope
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                    Status
                  </th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {keys.map((apiKey) => (
                  <tr
                    key={apiKey.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                  >
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      {apiKey.name}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {formatDate(apiKey.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {formatDate(apiKey.lastUsedAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {apiKey.permissionScope.slice(0, 3).map((scope) => (
                          <span
                            key={scope}
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                          >
                            {scope}
                          </span>
                        ))}
                        {apiKey.permissionScope.length > 3 && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                            +{apiKey.permissionScope.length - 3} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(apiKey.status)}`}
                      >
                        {apiKey.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-end">
                        {apiKey.status === 'active' && (
                          <button
                            onClick={() => openRevokeModal(apiKey)}
                            title="Revoke key"
                            className="p-1.5 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-50 dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Modals ──────────────────────────────────────────────────────────── */}

      {/* Create API Key Modal */}
      {activeModal === 'create' && (
        <ModalOverlay onClose={closeModal}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Create API Key
              </h2>
            </div>
            <div className="p-6 space-y-4">
              {modalError && (
                <p className="text-sm text-red-600 dark:text-red-400">{modalError}</p>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Key Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Production Integration"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Permission Scope <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                  {AVAILABLE_SCOPES.map((scope) => (
                    <label
                      key={scope.value}
                      className="flex items-center gap-2 cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        checked={selectedScopes.includes(scope.value)}
                        onChange={() => toggleScope(scope.value)}
                        className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
                        {scope.label}
                      </span>
                    </label>
                  ))}
                </div>
                {selectedScopes.length > 0 && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {selectedScopes.length} scope{selectedScopes.length !== 1 ? 's' : ''} selected
                  </p>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={closeModal}
                disabled={modalLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateKey}
                disabled={modalLoading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {modalLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Create Key
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* Show Generated Key Modal */}
      {activeModal === 'showKey' && generatedKey && (
        <ModalOverlay onClose={closeModal}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                API Key Created
              </h2>
            </div>
            <div className="p-6 space-y-4">
              {/* Warning */}
              <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <ShieldAlert className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                    This key will only be shown once
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                    Copy it now and store it securely. You will not be able to view it again.
                  </p>
                </div>
              </div>

              {/* Key Display */}
              <div className="relative">
                <div className="w-full px-3 py-3 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white break-all select-all">
                  {generatedKey}
                </div>
                <button
                  onClick={handleCopyKey}
                  className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="h-3 w-3 text-green-600 dark:text-green-400" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3" />
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* Revoke Confirmation Modal */}
      {activeModal === 'revoke' && selectedKey && (
        <ModalOverlay onClose={closeModal}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-sm w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Revoke API Key
              </h2>
            </div>
            <div className="p-6 space-y-3">
              {modalError && (
                <p className="text-sm text-red-600 dark:text-red-400">{modalError}</p>
              )}
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Are you sure you want to revoke the API key{' '}
                    <span className="font-semibold text-gray-900 dark:text-white">
                      "{selectedKey.name}"
                    </span>
                    ?
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    This action cannot be undone. Any integrations using this key will immediately lose access.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={closeModal}
                disabled={modalLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRevokeKey}
                disabled={modalLoading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {modalLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Revoke Key
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  )
}

// ─── Modal Overlay Component ───────────────────────────────────────────────────

function ModalOverlay({
  children,
  onClose,
}: {
  children: React.ReactNode
  onClose: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <div className="relative z-10">{children}</div>
    </div>
  )
}
